import { useMapboxLayerUtils } from '@/components/maps/composables/useMapboxLayerUtils'
import useMap from '@/composables/scopedStores/map/useMap'
import { baseMapStyleUrls, BasemapTweakHideBuiltEnv, BasemapTweaks, defaultBaseMap } from '@/config/map'
import { getEnv } from '@/composables/useEnv'
import { PMTiles, TileType } from 'pmtiles'
import {
  iconConfig,
  SimpleLayerSpecification,
  TBaseMapStyle,
  TBasemapTweak,
  TStyleLoadHandler
} from '@lifk/types'
import { ExpressionSpecification } from 'mapbox-gl'
import {
  computed,
  Directive,
  DirectiveBinding,
  inject,
  InjectionKey,
  provide,
  Ref,
  ref,
  watch
} from 'vue'

const IMAGERY_SOURCE = 'ortho-imagery'
const IMAGERY_LAYER = 'ortho-imagery-layer'

type TBaseMapStore = {
  baseMapStyle: Ref<TBaseMapStyle>
  baseMapStyleUrl: Ref<string>
  toggleBaseMapStyle: () => void
  setHiddenBaseMapTweaks: (toHide: TBasemapTweak[]) => void
  vStyleLoad: Directive<HTMLElement, TStyleLoadHandler>
}
export const BaseMapStoreKey = Symbol() as InjectionKey<TBaseMapStore>

export const provideBaseMap = () => {
  const map = useMap()
  const { getLayoutProperty, setLayoutProperty } = useMapboxLayerUtils()

  /*
    State
  */
  const baseMapStyle = ref<TBaseMapStyle>(defaultBaseMap)
  const baseMapStyleUrl = computed(() => baseMapStyleUrls[baseMapStyle.value])

  // mapbox StyleSpecification type is excessively deep/infinite, use `any` instead
  const styleSpec = ref<any | undefined>(map.getStyle() ?? undefined)

  const dataLayerTweaks = ref<TBasemapTweak[]>([])
  const imageryTweaks = ref<TBasemapTweak[]>([])
  const hiddenBaseMapTweaks = computed<TBasemapTweak[]>(() => [
    ...new Set([...dataLayerTweaks.value, ...imageryTweaks.value])
  ])
  const styleLoadHandlers = ref<TStyleLoadHandler[]>([])

  /*
    Setters
  */
  const toggleBaseMapStyle = () => {
    baseMapStyle.value = baseMapStyle.value === 'light' ? 'satellite' : 'light'
  }

  const tileTypeMime: Partial<Record<TileType, string>> = {
    [TileType.Png]: 'image/png',
    [TileType.Jpeg]: 'image/jpeg',
    [TileType.Webp]: 'image/webp',
    [TileType.Avif]: 'image/avif'
  }

  const addImageryOverlay = async () => {
    const url = getEnv('VITE_APP_ORTHO_IMAGERY_URL')
    if (!url || map.getSource(IMAGERY_SOURCE)) return
    const pmtiles = new PMTiles(url)
    const header = await pmtiles.getHeader()
    const mime = tileTypeMime[header.tileType as TileType] ?? 'image/png'
    map.addSource(IMAGERY_SOURCE, {
      type: 'custom',
      dataType: 'raster',
      tileSize: 256,
      minzoom: 14,
      maxzoom: 20,
      async loadTile({ z, x, y }: { z: number; x: number; y: number }, { signal }: { signal: AbortSignal }) {
        const tile = await pmtiles.getZxy(z, x, y, signal)
        if (!tile) {
          return new OffscreenCanvas(256, 256).transferToImageBitmap()
        }
        const bitmap = await createImageBitmap(new Blob([tile.data], { type: mime }))
        const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(bitmap, 0, 0)
        // Workaround: source tiles have no alpha channel so NODATA edges are
        // encoded as black. We zero out near-black pixels here. The proper fix
        // is to regenerate the PMTiles with --srcnodata "0 0 0" so tiles carry
        // their own alpha. Threshold may need tuning if dark valid pixels vanish.
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const { data } = imageData
        const w = canvas.width
        const h = canvas.height

        // NODATA is encoded as black (no alpha channel in source tiles). Rather
        // than zeroing all near-black pixels (which destroys valid dark vegetation),
        // flood-fill from the tile edges: only connected near-black pixels that
        // touch the border are NODATA; isolated dark pixels inside are valid.
        const nearBlack = new Uint8Array(w * h)
        for (let p = 0; p < w * h; p++) {
          const i = p * 4
          if (data[i] < 20 && data[i + 1] < 20 && data[i + 2] < 20) nearBlack[p] = 1
        }

        const reachable = new Uint8Array(w * h)
        const queue: number[] = []
        const enqueue = (p: number) => {
          if (nearBlack[p] && !reachable[p]) { reachable[p] = 1; queue.push(p) }
        }
        for (let x = 0; x < w; x++) { enqueue(x); enqueue((h - 1) * w + x) }
        for (let y = 1; y < h - 1; y++) { enqueue(y * w); enqueue(y * w + w - 1) }

        let head = 0
        while (head < queue.length) {
          const p = queue[head++]
          const x = p % w, y = (p / w) | 0
          if (x > 0) enqueue(p - 1)
          if (x < w - 1) enqueue(p + 1)
          if (y > 0) enqueue(p - w)
          if (y < h - 1) enqueue(p + w)
        }

        for (let p = 0; p < w * h; p++) {
          if (reachable[p]) data[p * 4 + 3] = 0
        }

        ctx.putImageData(imageData, 0, 0)
        return canvas.transferToImageBitmap()
      }
    } as any)
    if (!map.getLayer(IMAGERY_LAYER)) {
      map.addLayer({
        id: IMAGERY_LAYER,
        type: 'raster',
        source: IMAGERY_SOURCE,
        paint: { 'raster-fade-duration': 0, 'raster-resampling': 'nearest' }
      })
    }
  }

  const removeImageryOverlay = () => {
    if (map.getLayer(IMAGERY_LAYER)) map.removeLayer(IMAGERY_LAYER)
    if (map.getSource(IMAGERY_SOURCE)) map.removeSource(IMAGERY_SOURCE)
  }

  watch(baseMapStyle, (style) => {
    if (style === 'satellite') {
      imageryTweaks.value = [BasemapTweakHideBuiltEnv, 'Disable Terrain Exaggeration']
    } else {
      removeImageryOverlay()
      imageryTweaks.value = []
    }
  })

  const setHiddenBaseMapTweaks = (toHide: TBasemapTweak[]) => {
    dataLayerTweaks.value = toHide
  }

  const addEvent = (
    _el: HTMLElement,
    binding: DirectiveBinding<TStyleLoadHandler>
  ) => {
    const handler = binding.value
    styleLoadHandlers.value.push(handler)
  }

  const removeEvent = (
    _el: HTMLElement,
    binding: DirectiveBinding<TStyleLoadHandler>
  ) => {
    const handler = binding.value
    styleLoadHandlers.value = styleLoadHandlers.value.filter(
      (h) => h !== handler
    )
  }

  const vStyleLoad: Directive<HTMLElement, TStyleLoadHandler> = {
    mounted: addEvent,
    unmounted: removeEvent
  }

  /*
    Watchers / Apply state
  */
  const updateBasemapLayersVisibility = (
    tweaks: TBasemapTweak[],
    visible: boolean,
    styleLayers: SimpleLayerSpecification[]
  ) => {
    const layerIds = styleLayers
      .filter((layer) =>
        // Find layers that match any one of the active tweaks
        tweaks.some((tweak) => BasemapTweaks[tweak]?.match(layer))
      )
      .map(({ id }) => id)

    layerIds.forEach((id) => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
      }
    })
  }

  const applyBaseMapTweaks = (
    nextHidden: TBasemapTweak[],
    prevHidden: TBasemapTweak[]
  ) => {
    // Update layer visibilities
    const toShow = prevHidden.filter((prev) => !nextHidden.includes(prev))
    const toHide = nextHidden.filter((next) => !prevHidden.includes(next))
    const styleLayers: SimpleLayerSpecification[] = (styleSpec.value?.layers ??
      []) as SimpleLayerSpecification[]

    updateBasemapLayersVisibility(toShow, true, styleLayers)
    updateBasemapLayersVisibility(toHide, false, styleLayers)

    // Enable/Disable terrain exaggeration (only set if there is a change and style is ready)
    const enableTerrain = toShow.includes('Disable Terrain Exaggeration')
    const disableTerrain = toHide.includes('Disable Terrain Exaggeration')
    if ((enableTerrain || disableTerrain) && map.isStyleLoaded()) {
      if (disableTerrain) {
        map.setTerrain(null as any)
      } else {
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 } as any)
      }
    }
  }

  watch(hiddenBaseMapTweaks, applyBaseMapTweaks)

  watch(baseMapStyleUrl, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      // Note: erases all dynamic layers
      map.setStyle(baseMapStyleUrl.value, {
        diff: false
        // config: { basemap: { 'fill-extrusion-opacity': 0.3 } }
      } as any)
    }
  })

  // Replaces Mapbox POI icons with custom icons
  const replaceMapboxIcons = async (): Promise<void> => {
    try {
      const loadPromises = Object.entries(iconConfig).map(
        ([makiValue, config]) => {
          const iconPath = config[baseMapStyle.value]
          const imageId = `custom-${makiValue}-icon-${baseMapStyle.value}`

          return new Promise<{
            makiValue: string
            imageId: string
            layer: string
          }>((resolve, reject) => {
            map.loadImage(iconPath ?? '', (error, image) => {
              if (error) {
                console.warn(`Error loading icon for "${makiValue}":`, error)
                reject(error)
                return
              }

              if (image) {
                if (!map.hasImage(imageId)) {
                  map.addImage(imageId, image)
                }
                resolve({ makiValue, imageId, layer: config.layer })
              } else {
                reject(new Error(`Failed to load image for ${makiValue}`))
              }
            })
          })
        }
      )

      const loadedIcons = await Promise.allSettled(loadPromises)

      const successfulLoads = loadedIcons
        .filter(
          (
            result
          ): result is PromiseFulfilledResult<{
            makiValue: string
            imageId: string
            layer: string
          }> => result.status === 'fulfilled'
        )
        .map((result) => result.value)

      if (successfulLoads.length === 0) {
        console.warn('No icons loaded successfully')
        return
      }

      // Group icons by target layer
      const iconsByLayer: Record<
        string,
        { makiValue: string; imageId: string }[]
      > = {}
      for (const { layer, makiValue, imageId } of successfulLoads) {
        if (!iconsByLayer[layer]) iconsByLayer[layer] = []
        iconsByLayer[layer].push({ makiValue, imageId })
      }

      // Apply custom icons per layer
      for (const [layerName, icons] of Object.entries(iconsByLayer)) {
        const currentIconImage = getLayoutProperty(layerName, 'icon-image')
        const caseExpression: ExpressionSpecification = ['case']

        icons.forEach(({ makiValue, imageId }) => {
          caseExpression.push(['==', ['get', 'maki'], makiValue])
          caseExpression.push(imageId)
        })

        // Fallback
        caseExpression.push(currentIconImage || ['get', 'maki'])

        setLayoutProperty(layerName, 'icon-image', caseExpression)

        console.debug(
          `✅ Icons applied to layer "${layerName}": ${icons.map((i) => i.makiValue).join(', ')}`
        )
      }
    } catch (error) {
      console.error('Error in replaceMapboxIcons:', error)
    }
  }

  map.on('style.load', () => {
    styleSpec.value = map.getStyle() ?? undefined
    applyBaseMapTweaks(hiddenBaseMapTweaks.value, [])

    styleLoadHandlers.value.forEach((handler) => {
      handler()
    })

    if (baseMapStyle.value === 'satellite') {
      replaceMapboxIcons()
    }

    if (baseMapStyle.value === 'satellite') {
      addImageryOverlay()
    }
  })

  /*
    Provide
  */
  // For use by children of MapContext.vue
  provide(BaseMapStoreKey, {
    baseMapStyle,
    baseMapStyleUrl,
    toggleBaseMapStyle,
    setHiddenBaseMapTweaks,
    vStyleLoad
  })
}

// Use in scenarios that may exist outside of Map context (ie- UIBanner.vue)
export function useOptionalBaseMap(): TBaseMapStore | undefined {
  // undefined default value suppresses console warnings
  return inject(BaseMapStoreKey, undefined)
}

// Use for scenarios that are guaranteed to exist within Map context (most cases)
export default function useBaseMap(): TBaseMapStore {
  return useOptionalBaseMap()!
}
